import { createSelector } from 'reselect';
import { isCoachedCourse } from '../../coaching/lib/isCoachedCourse.js';
import { isB2BUser } from '../../auth/lib/onboardingStatus.js';
import {
  isAssessmentCourse,
  isLiveCourse,
  isOnlineLessonCourse,
} from '../../courses/lib/courseClassification.js';

const selectFeatureVisibilityState = (state) => state.featureVisibility;

const selectCourses = (state) => state.coursesList?.courses ?? [];
const selectAuthUser = (state) => state.auth?.user;
const selectAuthOrg = (state) => state.auth?.org;

export const selectIsB2BUser = createSelector(
  [selectAuthUser, selectAuthOrg],
  (user, org) => isB2BUser({ user, org }),
);

export const selectIsB2CUser = createSelector(
  selectIsB2BUser,
  (isB2B) => !isB2B,
);

export const selectFeatureVisibilityStatus = createSelector(
  selectFeatureVisibilityState,
  (state) => state?.status ?? 'idle',
);

export const selectFeatureVisibilityReady = createSelector(
  selectFeatureVisibilityState,
  (state) => state?.initialized === true || ['succeeded', 'failed'].includes(state?.status),
);

// Native navigation requires a successful response for this session. Keep
// using those flags during refreshes, including a failed background refresh.
export const selectFeatureVisibilityLoaded = createSelector(
  selectFeatureVisibilityState,
  (state) => state?.hasLoaded === true || state?.status === 'succeeded',
);

export const selectFeatureFlags = createSelector(
  selectFeatureVisibilityState,
  (state) => state?.flags ?? {},
);

export const selectCanShowOnlineLessons = createSelector(
  selectFeatureFlags,
  (flags) => flags.onlineLessons !== false,
);

export const selectMyAssessmentEnabled = createSelector(
  selectFeatureFlags,
  (flags) => flags.myAssessment !== false,
);

export const selectCanShowCertificates = createSelector(
  selectFeatureFlags,
  (flags) => flags.certificates !== false,
);

export const selectCanShowCoachCalls = createSelector(
  selectFeatureFlags,
  (flags) => flags.showCoachCalls !== false,
);

export const selectCanShowLiveClass = createSelector(
  selectFeatureFlags,
  (flags) => flags.showLiveClass === true,
);

export const selectCanShowProgress = createSelector(
  selectFeatureFlags,
  (flags) => flags.showScore !== false,
);

export const selectCanShowMyCourses = createSelector(
  [selectCanShowOnlineLessons, selectCanShowLiveClass, selectMyAssessmentEnabled, selectIsB2CUser],
  (showOnlineLessons, showLiveClass, myAssessmentEnabled, isB2C) =>
    showOnlineLessons || showLiveClass || (isB2C && myAssessmentEnabled),
);

export const selectHasAssessmentCourse = createSelector(
  selectCourses,
  (courses) => courses.some(isAssessmentCourse),
);

export const selectCanShowMyAssessments = createSelector(
  [selectMyAssessmentEnabled, selectIsB2BUser],
  (myAssessmentEnabled, isB2B) => myAssessmentEnabled && isB2B,
);

export const selectDefaultLearnPath = createSelector(
  [selectCanShowMyCourses, selectCanShowMyAssessments],
  (canShowMyCourses, canShowMyAssessments) => {
    if (canShowMyCourses) return '/courses';
    if (canShowMyAssessments) return '/assessments';
    return '/unavailable';
  },
);

export const selectCanShowSettingsHistory = createSelector(
  selectFeatureFlags,
  (flags) => flags.showSettingsHistory === true,
);

export const selectCanShowSettings = createSelector(
  selectFeatureFlags,
  (flags) => flags.settings !== false,
);

export const selectFeatureVisibleCourses = createSelector(
  [selectCourses, selectCanShowOnlineLessons, selectCanShowCoachCalls, selectCanShowLiveClass],
  (courses, showOnlineLessons, showCoachCalls, showLiveClass) =>
    courses.filter((course) => {
      if (isCoachedCourse(course)) return showCoachCalls;
      if (isLiveCourse(course)) return showLiveClass;
      if (isOnlineLessonCourse(course)) return showOnlineLessons;
      return true;
    }),
);

export const selectHasVisibleCoachCourse = createSelector(
  [selectFeatureVisibleCourses, selectCanShowCoachCalls],
  (courses, showCoachCalls) => showCoachCalls && courses.some(isCoachedCourse),
);

export const selectFeatureVisibleLearningCourses = createSelector(
  [selectFeatureVisibleCourses, selectIsB2CUser],
  (courses, isB2C) => courses.filter((course) =>
    !isCoachedCourse(course) && (isB2C || !isAssessmentCourse(course)),
  ),
);

export const selectFeatureVisibleCoachCourses = createSelector(
  [selectFeatureVisibleCourses, selectCanShowCoachCalls],
  (courses, showCoachCalls) => (showCoachCalls ? courses.filter(isCoachedCourse) : []),
);

export const selectFeatureVisibleAssessmentCourses = createSelector(
  selectFeatureVisibleCourses,
  (courses) => courses.filter(isAssessmentCourse),
);
